import React, { useState, useEffect, lazy, Suspense } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
  CircularProgress,
  Box
} from '@material-ui/core'
import { ATTACHMENT_TYPES } from '../utils/attachmentUtils'
import { useAttachment } from '../contexts/AttachmentContext'
import { useAIThreadAttachment } from '../../../../api/denaliAssistant'
import Alert from '../../../../components/atoms/Alert'
import OpalHeading from '../../../../components/opal/OpalHeading'
import SydButton from '../../../../components/commonDesign/Button'
import { AttachmentPanel } from './AttachmentPanel'

// Lazy load viewers for better performance
const QueryResultsViewer = lazy(() => import('./viewers/QueryResultsViewer'))
const JSXViewer = lazy(() => import('./viewers/JSXViewer'))
const DQLViewer = lazy(() => import('./viewers/DQLViewer'))
const CSVViewer = lazy(() => import('./viewers/CSVViewer'))
const EmailViewer = lazy(() => import('./viewers/EmailViewer'))
const MarkdownViewer = lazy(() => import('./viewers/MarkdownViewer'))
const ToolCallViewer = lazy(() => import('./viewers/ToolCallViewer'))
const DefaultViewer = lazy(() => import('./viewers/DefaultViewer'))

const useStyles = makeStyles((theme) => ({
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
    padding: theme.spacing(4)
  },
  errorContainer: {
    padding: theme.spacing(3)
  },
  viewerContainer: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative'
  }
}))

export function AttachmentViewer () {
  const classes = useStyles()
  const { selectedAttachment, isOpen, closeAttachment } = useAttachment()
  const [viewMode, setViewMode] = useState('preview')

  // Determine if this is an inline attachment or needs fetching
  const isInlineAttachment = selectedAttachment?.inline && selectedAttachment?.data
  const needsRemoteFetch = !selectedAttachment?.inline && !!selectedAttachment?.threadId && !!selectedAttachment?.attachmentId

  // Use React Query for fetching non-inline attachments
  const { data: remoteData, error: fetchError, isLoading } = useAIThreadAttachment(
    selectedAttachment?.threadId,
    selectedAttachment?.attachmentId,
    {
      enabled: isOpen && needsRemoteFetch
    }
  )

  // Reset view mode when attachment changes
  useEffect(() => {
    // Set default view mode based on content type and subType
    if (selectedAttachment?.contentType === ATTACHMENT_TYPES.TOOL_CALL) {
      const subType = selectedAttachment?.data?.subType
      // Default to specialized views if available
      if (subType === 'get_catalog_schema') {
        setViewMode('catalog')
      } else if (subType === 'execute_dql_query') {
        setViewMode('query')
      } else {
        setViewMode('details')
      }
    } else {
      setViewMode('preview')
    }
  }, [selectedAttachment?.attachmentId, selectedAttachment?.contentType, selectedAttachment?.data?.subType])

  // Get the attachment data
  const attachmentData = isInlineAttachment
    ? selectedAttachment.data
    : remoteData?.attachment?.data || remoteData?.data || remoteData

  // Determine error state
  const error = fetchError?.message ||
    (selectedAttachment?.inline && !selectedAttachment?.data ? 'Attachment data is not available' : null)

  // Get the appropriate viewer component
  const getViewer = () => {
    // For remote attachments, show loading state
    if (needsRemoteFetch && isLoading) {
      return (
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      )
    }

    // Show errors
    if (error) {
      return (
        <Box className={classes.errorContainer}>
          <Alert severity='error'>
            <OpalHeading>Unable to load attachment</OpalHeading>
            {error}
          </Alert>
        </Box>
      )
    }

    // No data available
    if (!attachmentData) {
      return null
    }

    const viewerProps = {
      data: attachmentData,
      metadata: selectedAttachment
    }

    switch (selectedAttachment?.contentType) {
      case ATTACHMENT_TYPES.QUERY_RESULTS:
        return <QueryResultsViewer {...viewerProps} />

      case ATTACHMENT_TYPES.JSX_CODE:
        return (
          <JSXViewer
            {...viewerProps}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
        )

      case ATTACHMENT_TYPES.DQL_QUERY:
      case ATTACHMENT_TYPES.SQL_QUERY:
        return <DQLViewer {...viewerProps} />

      case ATTACHMENT_TYPES.CSV_DATA:
        return <CSVViewer {...viewerProps} />

      case ATTACHMENT_TYPES.ARTIFACT_EMAIL:
        return <EmailViewer {...viewerProps} />

      case ATTACHMENT_TYPES.ARTIFACT_AGENDA:
      case ATTACHMENT_TYPES.ARTIFACT_TALKING_POINTS:
      case ATTACHMENT_TYPES.ARTIFACT_PROPOSAL:
      case ATTACHMENT_TYPES.ARTIFACT_SUMMARY:
        return <MarkdownViewer {...viewerProps} />

      case ATTACHMENT_TYPES.TOOL_CALL:
        return (
          <ToolCallViewer
            {...viewerProps}
            viewMode={viewMode}
          />
        )

      default:
        return <DefaultViewer {...viewerProps} />
    }
  }

  // Create header actions for JSX content
  const isJSXContent = selectedAttachment?.contentType === ATTACHMENT_TYPES.JSX_CODE
  const isDqlToolCall = selectedAttachment?.contentType === ATTACHMENT_TYPES.TOOL_CALL &&
                        selectedAttachment?.data?.subType === 'execute_dql_query'
  const isCatalogSchemaToolCall = selectedAttachment?.contentType === ATTACHMENT_TYPES.TOOL_CALL &&
                                  selectedAttachment?.data?.subType === 'get_catalog_schema'

  const headerActions = isJSXContent ? (
    <>
      <SydButton
        size='sm'
        variant={viewMode === 'preview' ? 'primary' : 'ghost'}
        onClick={() => setViewMode('preview')}
        icon='visibility'
      >
        Preview
      </SydButton>
      <SydButton
        size='sm'
        variant={viewMode === 'source' ? 'primary' : 'ghost'}
        onClick={() => setViewMode('source')}
        icon='code'
      >
        Source
      </SydButton>
    </>
  ) : isDqlToolCall ? (
    <>
      <SydButton
        size='sm'
        variant={viewMode === 'query' ? 'primary' : 'ghost'}
        onClick={() => setViewMode('query')}
        icon='dataObject'
      >
        Query View
      </SydButton>
      <SydButton
        size='sm'
        variant={viewMode === 'details' ? 'primary' : 'ghost'}
        onClick={() => setViewMode('details')}
        icon='description'
      >
        Details View
      </SydButton>
    </>
  ) : isCatalogSchemaToolCall ? (
    <>
      <SydButton
        size='sm'
        variant={viewMode === 'catalog' ? 'primary' : 'ghost'}
        onClick={() => setViewMode('catalog')}
        icon='viewList'
      >
        Catalog View
      </SydButton>
      <SydButton
        size='sm'
        variant={viewMode === 'details' ? 'primary' : 'ghost'}
        onClick={() => setViewMode('details')}
        icon='description'
      >
        Details View
      </SydButton>
    </>
  ) : null

  return (
    <AttachmentPanel
      open={isOpen}
      onClose={closeAttachment}
      title={selectedAttachment?.title || 'Attachment'}
      width='600px'
      headerActions={headerActions}
    >
      <div className={classes.viewerContainer} key={selectedAttachment?.attachmentId}>
        <Suspense fallback={
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        }
        >
          {getViewer()}
        </Suspense>
      </div>
    </AttachmentPanel>
  )
}
